import { IconButton } from '@mui/material';
import { FC, ReactElement } from 'react';

import { useLogout } from 'components/auth/hooks/useLogout';
import AccountIcon from 'components/system/Icons/AccountIcon';
import AiExamIcon from 'components/system/Icons/AiExamIcon';
import ColorAccountIcon from 'components/system/Icons/ColorAccountIcon';
import ColorAiExamIcon from 'components/system/Icons/ColorAiExamIcon';
import ColorCreatePatientIcon from 'components/system/Icons/ColorCreatePatientIcon';
import ColorDashboardIcon from 'components/system/Icons/ColorDashboardIcon';
import ColorPatientViewIcon from 'components/system/Icons/ColorPatientViewIcon';
import ColorStudyManagerIcon from 'components/system/Icons/ColorStudyManagerIcon';
import CreatePatientIcon from 'components/system/Icons/CreatePatientIcon';
import DashboardIcon from 'components/system/Icons/DashboardIcon';
import LogOutIcon from 'components/system/Icons/LogOutIcon';
import PatientViewIcon from 'components/system/Icons/PatientViewIcon';
import StudyManagerIcon from 'components/system/Icons/StudyManagerIcon';
import ListItemLink from 'components/system/ListItemLink/ListItemLink';
import { TechnologyLogo } from 'components/system/TechnologyLogo/TechnologyLogo';
import { Tooltip } from 'components/system/Tooltip';
import { Technology } from 'utils/products';
import { PAGE_TITLES } from '../PageBase/constants';
import {
  AppVersionText,
  CustomDrawer,
  CustomList,
  CustomListItemIcon,
  CustomListItemText,
  CustomListLast,
  LastWrapper,
  LogoWrapper,
} from './appDrawer.styles';

interface IAppDrawer {
  open: boolean;
  handleDrawerToggle: () => void;
  activePage: string;
}

export const DRAWER_OPTIONS: {
  url: keyof typeof PAGE_TITLES;
  icon: {
    default: ReactElement;
    active: ReactElement;
  };
}[] = [
  {
    url: 'dashboard',
    icon: {
      default: <DashboardIcon titleAccess="Dashboard" />,
      active: <ColorDashboardIcon titleAccess="Dashboard Active" />,
    },
  },
  {
    url: 'my-account',
    icon: {
      default: <AccountIcon titleAccess="My Account" />,
      active: <ColorAccountIcon titleAccess="My Account Active" />,
    },
  },
  {
    url: 'create-ai-exam',
    icon: {
      default: <AiExamIcon titleAccess="Create AI Exam" />,
      active: <ColorAiExamIcon titleAccess="Create AI Exam Active" />,
    },
  },
  {
    url: 'create-patient',
    icon: {
      default: <CreatePatientIcon titleAccess="Create Patient" />,
      active: <ColorCreatePatientIcon titleAccess="Create Patient Active" />,
    },
  },
  {
    url: 'study-manager',
    icon: {
      default: <StudyManagerIcon titleAccess="Study Manager" />,
      active: <ColorStudyManagerIcon titleAccess="Study Manager Active" />,
    },
  },
  {
    url: 'patients',
    icon: {
      default: <PatientViewIcon titleAccess="Patient List" />,
      active: <ColorPatientViewIcon titleAccess="Patient List Active" />,
    },
  },
];

const AppDrawer: FC<IAppDrawer> = ({
  open,
  handleDrawerToggle,
  activePage,
}) => {
  const { logout } = useLogout();

  return (
    <CustomDrawer variant="permanent" anchor="left" open={open}>
      <LogoWrapper>
        <IconButton
          color="inherit"
          aria-label="toggle drawer"
          onClick={handleDrawerToggle}
          sx={{
            height: '65px',
          }}
        >
          <TechnologyLogo
            sx={
              !open
                ? {
                    width: 'auto',
                    height: '65px',
                    marginLeft: 5,
                  }
                : { width: '166px', maxHeight: '56px' }
            }
            version={open ? 'full' : 'icon'}
          />
        </IconButton>
      </LogoWrapper>
      <CustomList>
        {DRAWER_OPTIONS.map(({ url, icon }) => {
          const text = PAGE_TITLES[url];

          return (
            <ListItemLink to={`/app/${url}`} key={url}>
              <CustomListItemIcon>
                <Tooltip placement="right" title={text} isDisabled={open}>
                  <IconButton
                    aria-label={text}
                    edge="start"
                    size="large"
                    color={activePage === url ? 'primary' : 'inherit'}
                  >
                    {activePage === url ? icon.active : icon.default}
                  </IconButton>
                </Tooltip>
              </CustomListItemIcon>
              <CustomListItemText
                primary={text}
                isActive={activePage === url}
              />
            </ListItemLink>
          );
        })}
      </CustomList>

      <LastWrapper open={open}>
        <AppVersionText open={open} variant="textSm">
          Version {Technology.version}
        </AppVersionText>
        <CustomListLast>
          <ListItemLink
            to="/"
            key={'Logout'}
            onClick={(event) => {
              event.preventDefault();
              logout();
            }}
          >
            <CustomListItemIcon>
              <Tooltip placement="right" title="Log Out" isDisabled={open}>
                <IconButton edge="start" size="large" aria-label="Log Out">
                  <LogOutIcon titleAccess="Log Out" />
                </IconButton>
              </Tooltip>
            </CustomListItemIcon>
            <CustomListItemText primary={'Log Out'} />
          </ListItemLink>
        </CustomListLast>
      </LastWrapper>
    </CustomDrawer>
  );
};

export default AppDrawer;
